import {
  type ChangeEvent,
  type FocusEvent,
  forwardRef,
  memo,
  type MutableRefObject,
  useCallback,
  useEffect,
} from "react";

import { useTranslation } from "react-i18next";
import { PopperTooltip } from "~/common/components/Tooltip";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { Button, IconButton, styled } from "~/common/mui";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

interface SearchInputProps {
  value: string;
  autofocus?: boolean;
  onChange?: (text: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onClear?: () => void;
  placeholder?: string;
  variant?: "header" | "cad";
}

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ value: searchText, autofocus, onChange, onBlur, onFocus, onClear, placeholder, variant }, ref) => {
    const { t } = useTranslation();
    const changeCb = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const text = e.currentTarget.value.trimLeft();
        if (onChange) {
          onChange(text);
        }
      },
      [onChange],
    );

    const focusController = (e: FocusEvent<HTMLInputElement>) => {
      const target = e.currentTarget as HTMLInputElement;
      if (autofocus && target) {
        target.select();
      }
      onFocus?.();
    };

    const isDesktop = useIsMinDesktopSize();

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        const inputRef = ref as MutableRefObject<HTMLInputElement>;
        if (event.key === "Escape") {
          event.preventDefault();
          inputRef.current.blur();
        }
      };

      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, []);

    return (
      <SearchInputRoot $variant={variant}>
        <Input
          ref={ref}
          value={searchText}
          maxLength={100}
          id={"searchBoxInput"}
          name="text"
          placeholder={placeholder}
          onChange={changeCb}
          onBlur={onBlur}
          autoFocus={autofocus}
          onFocus={focusController}
          autoComplete="off"
          role="searchbox"
          // TODO - experiment with type = search
        />
        {variant !== "cad" && searchText && (
          <IconButton
            size="small"
            className={"scha__search-input-clear"}
            aria-label={t("searchPage.refinement.button.clear")}
            onClick={onClear}
            type={"button"}
          >
            <CloseIcon />
          </IconButton>
        )}
        <PopperTooltip hideTooltip={!isDesktop || variant === "cad"} tooltipContent={t("orderListPage.searchBtn")}>
          <SearchButton
            disabled={!searchText}
            aria-label={t("generic.button.search")}
            type={"submit"}
            id={"searchBoxSubmit"}
            $variant={variant}
          >
            <SearchIcon sx={{ color: (theme) => theme.palette.primary.onContainer }} />
          </SearchButton>
        </PopperTooltip>
      </SearchInputRoot>
    );
  },
);

export default memo(SearchInput);

const SearchInputRoot = styled("div", {
  shouldForwardProp: (prop) => prop !== "$variant",
})<{ $variant?: SearchInputProps["variant"] }>(({ theme, $variant }) => ({
  display: "flex",
  backgroundColor: theme.palette.surfaceContainer.lowest,
  gap: theme.spacing(0.5),
  transition: "border-color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  width: "100%",
  height: theme.spacing(4.5),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  [theme.breakpoints.up("tablet")]: {
    height: theme.spacing(5),
  },
  "&:hover": {
    borderColor: theme.palette.text.primary,
  },
  "&:focus-within": {
    borderColor: theme.palette.primary.main,
  },
  ".scha__search-input-clear": {
    display: "flex",
    alignSelf: "center",
  },
  ...($variant === "cad" && {
    flexDirection: "row-reverse",
    paddingLeft: 0,
    overflow: "hidden",
    height: theme.spacing(5),
    "> input": {
      paddingRight: theme.spacing(1),
    },
  }),
  ...($variant === "header" && {
    height: theme.spacing(5),
    overflow: "hidden",
  }),
}));

const Input = styled("input")(({ theme }) => ({
  border: "none",
  flex: 1,
  minWidth: 0,
  width: theme.spacing(6.25),
  paddingLeft: theme.spacing(2),
  // Keeps the input at 16px, preventing iOS Safari from zooming on focus.
  ...theme.typography.bodyLarge,
  height: "100%",
  // some real mobile browsers (WebKit) wrap the input value if these aren't forced explicitly
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  wordBreak: "normal",
  overflowWrap: "normal",
  "&::placeholder": {
    color: theme.palette.text.label,
    ...theme.typography.bodyLarge,
  },
}));

const SearchButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "$variant",
})<{ $variant?: SearchInputProps["variant"] }>(({ theme, $variant }) => ({
  minHeight: "unset",
  borderRadius: 0,
  background: `${theme.palette.primary.container} !important`,
  color: theme.palette.primary.onContainer,
  minWidth: "unset",
  [theme.breakpoints.up("desktop-s")]: {
    paddingInline: theme.spacing(3),
  },
  ...($variant === "header" && {
    height: "100%",
    width: 48,
    "&:hover": {
      background: `${theme.palette.primary.main} !important`,
      boxShadow: "none",
      "& .MuiSvgIcon-root": {
        color: theme.palette.primary.contrastText,
      },
    },
    [theme.breakpoints.up("desktop-s")]: {
      width: 56,
    },
  }),
}));
